import {
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Grid,
  Typography,
  IconButton,
  Box,
} from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import { InputField } from "../../../../../../components/Form/InputField";
import SelectFieldWithSearch from "../../../../../../components/Form/SelectFieldWithSearch";
import CloseIcon from "@mui/icons-material/Close";
import {
  notifyError,
  notifySuccess,
} from "../../../../../../components/ToastContainer/Toast/Toast";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { useState } from "react";
import {
  createCalculateShipment,
  createShipment,
} from "../../../../../../service/order/orderService";

interface ShipmentFormValues {
  weightUnit: string;
  weightValue: string;
  unit: string;
  length: string;
  width: string;
  height: string;
}

const weightUnits = [
  { label: "Kilogram", value: "kg" },
  { label: "Gram", value: "g" },
];

const dimensionUnits = [
  { label: "Centimeter", value: "cm" },
  { label: "Meter", value: "m" },
];

const validationSchema = Yup.object().shape({
  weightUnit: Yup.string().required("Weight unit is required"),
  weightValue: Yup.number()
    .typeError("Weight value must be a number")
    .min(0, "Negative numbers are not allowed")
    .max(100, "Please keep it within 100")
    .required("Weight value is required")
    .test(
      "max-2-decimals",
      "Only up to 2 decimal places are allowed",
      (value) =>
        value === undefined || /^\d+(\.\d{1,2})?$/.test(value.toString())
    ),

  length: Yup.number()
    .typeError("Length must be a number")
    .min(0, "Negative numbers are not allowed")
    .max(99999, "Enter up to 5 digits")
    .test(
      "max-2-decimals",
      'Please enter a valid number up to 2 decimal places. "0.00" is not allowed.',
      (value) =>
        value === undefined ||
        /^(?!0\.00$)(0|[1-9][0-9]{0,4})(\.\d{1,2})?$/.test(value.toString())
    ),
  width: Yup.number()
    .typeError("Width must be a number")
    .min(0, "Negative numbers are not allowed")
    .max(99999, "Enter up to 5 digits")
    .test(
      "max-2-decimals",
      'Please enter a valid number up to 2 decimal places. "0.00" is not allowed.',
      (value) =>
        value === undefined ||
        /^(?!0\.00$)(0|[1-9][0-9]{0,4})(\.\d{1,2})?$/.test(value.toString())
    ),
  height: Yup.number()
    .typeError("Height must be a number")
    .min(0, "Negative numbers are not allowed")
    .max(99999, "Enter up to 5 digits")
    .test(
      "max-2-decimals",
      'Please enter a valid number up to 2 decimal places. "0.00" is not allowed.',
      (value) =>
        value === undefined ||
        /^(?!0\.00$)(0|[1-9][0-9]{0,4})(\.\d{1,2})?$/.test(value.toString())
    ),
});

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.common.black,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

export default function ShipmentForm({ handleClose, invoiceId }) {
  const [isTableOpen, setIsTableOpen] = useState(false);
  const [isChargesCalculated, setIsChargesCalculated] = useState(false);
  const [shipmentData, setShipmentData] = useState(null);
  return (
    <>
      <DialogTitle id="alert-dialog-title">Create Shipment</DialogTitle>
      <IconButton
        onClick={handleClose}
        aria-label="close"
        sx={{
          position: "absolute",
          right: 8,
          top: 10,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ backgroundColor: "white" }}>
        <DialogContentText id="alert-dialog-description">
          <Box sx={{ color: "#0B0E14" }}>
            <Formik<ShipmentFormValues>
              initialValues={{
                weightUnit: "",
                weightValue: "",
                unit: "",
                length: "",
                width: "",
                height: "",
              }}
              validationSchema={validationSchema}
              onSubmit={async (values, { resetForm }) => {
                try {
                  const postPayload = {
                    length: values.length === "" ? 0 : Number(values.length),
                    width: values.width === "" ? 0 : Number(values.width),
                    height: values.height === "" ? 0 : Number(values.height),
                    ...(values.unit && { unit: values.unit }),
                    weight: Number(values.weightValue),
                    invoice_id: invoiceId,
                  };
                  const response = await createShipment(postPayload);
                  if ([200, 201].includes(response.status)) {
                    notifySuccess("Successfully Created");
                    resetForm();
                    handleClose();
                  }
                } catch (err) {
                  notifyError("Something went wrong!");
                }
              }}
            >
              {({
                values,
                handleChange,
                setFieldValue,
                validateForm,
                setTouched,
              }: FormikProps<ShipmentFormValues>) => (
                <Form>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Weight
                  </Typography>
                  <Grid container>
                    <Grid item xs={6} sx={{ px: 2, mb: 2 }}>
                      <SelectFieldWithSearch
                        label="Weight Unit"
                        name="weightUnit"
                        value={values.weightUnit}
                        options={weightUnits}
                        onChange={handleChange}
                        isRequired
                        size="large"
                      />
                    </Grid>
                    <Grid item xs={6} sx={{ px: 2, mb: 2 }}>
                      <InputField
                        label="Weight Value"
                        name="weightValue"
                        value={values.weightValue}
                        onChange={handleChange}
                        placeholder="Enter weight value"
                        isRequired
                      />
                    </Grid>
                  </Grid>

                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Dimensions
                  </Typography>
                  <Grid container>
                    <Grid item xs={6} sx={{ px: 2, mb: 2 }}>
                      <SelectFieldWithSearch
                        label="Unit"
                        name="unit"
                        value={values.unit}
                        options={dimensionUnits}
                        onChange={handleChange}
                        size="large"
                      />
                    </Grid>
                    <Grid item xs={6} sx={{ px: 2, mb: 2 }}>
                      <InputField
                        label="Length"
                        name="length"
                        disabled={values.unit === ""}
                        value={values.length}
                        onChange={handleChange}
                        placeholder="Enter length"
                      />
                    </Grid>
                    <Grid item xs={6} sx={{ px: 2, mb: 2 }}>
                      <InputField
                        label="Width"
                        name="width"
                        disabled={values.unit === ""}
                        value={values.width}
                        onChange={handleChange}
                        placeholder="Enter width"
                      />
                    </Grid>
                    <Grid item xs={6} sx={{ px: 2, mb: 2 }}>
                      <InputField
                        label="Height"
                        name="height"
                        disabled={values.unit === ""}
                        value={values.height}
                        onChange={handleChange}
                        placeholder="Enter height"
                      />
                    </Grid>
                  </Grid>

                  {isTableOpen && shipmentData && (
                    <TableContainer component={Paper} sx={{ mt: 2, mb: 2 }}>
                      <Table aria-label="customized table">
                        <TableHead>
                          <TableRow>
                            <StyledTableCell>Service Type</StyledTableCell>
                            <StyledTableCell align="right">
                              Zone
                            </StyledTableCell>
                            <StyledTableCell align="right">
                              Shipping charges (₹)
                            </StyledTableCell>
                            <StyledTableCell align="right">
                              COD Charges (₹)
                            </StyledTableCell>
                            <StyledTableCell align="right">
                              Taxes (₹)
                            </StyledTableCell>
                            <StyledTableCell align="right">
                              Total (₹)
                            </StyledTableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody sx={{ backgroundColor: "white" }}>
                          <StyledTableRow>
                            <StyledTableCell component="th" scope="row">
                              {shipmentData?.type || "N/A"}
                            </StyledTableCell>
                            <StyledTableCell align="right">
                              {shipmentData?.zone || "N/A"}
                            </StyledTableCell>
                            <StyledTableCell align="right">
                              {shipmentData?.shippingCharge || "0.00"}
                            </StyledTableCell>
                            <StyledTableCell align="right">
                              {shipmentData?.codCharge || "0.00"}
                            </StyledTableCell>
                            <StyledTableCell align="right">
                              {shipmentData?.taxes || "0.00"}
                            </StyledTableCell>
                            <StyledTableCell align="right">
                              {shipmentData?.total || "0.00"}
                            </StyledTableCell>
                          </StyledTableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}

                  <DialogActions sx={{ mt: 3 }}>
                    <Button
                      onClick={handleClose}
                      color="secondary"
                      variant="outlined"
                    >
                      Cancel
                    </Button>

                    {!isChargesCalculated ? (
                      <Button
                        onClick={async () => {
                          const errors = await validateForm();

                          if (Object.keys(errors).length > 0) {
                            setTouched({
                              weightUnit: true,
                              weightValue: true,
                              unit: true,
                              length: true,
                              width: true,
                              height: true,
                            });
                            notifyError("Please fill required fields");
                            return;
                          }
                          const weight = {
                            unit: values.weightUnit,
                            value: parseFloat(values.weightValue) || 0,
                          };

                          const dimensions = {
                            unit: values.unit,
                            length: parseFloat(values.length) || 0,
                            width: parseFloat(values.width) || 0,
                            height: parseFloat(values.height) || 0,
                          };

                          const payload = {
                            invoice_id: invoiceId,
                            shipping_direction: "FORWARD",
                            weight,
                            dimensions,
                          };

                          try {
                            const response = await createCalculateShipment(
                              payload
                            );
                            const responseData = response?.data?.data;
                            if (Object.values(responseData).length > 0) {
                              setShipmentData(responseData);
                              setIsTableOpen(true);
                              setIsChargesCalculated(true);
                            } else {
                              setShipmentData(null);
                              setIsTableOpen(false);
                              setIsChargesCalculated(false);
                            }
                          } catch (error) {
                            notifyError("Failed to calculate shipment charges");
                          }
                        }}
                        color="primary"
                        variant="outlined"
                      >
                        Shipment Calculation
                      </Button>
                    ) : (
                      <Button type="submit" color="primary" variant="contained">
                        Create
                      </Button>
                    )}
                  </DialogActions>
                </Form>
              )}
            </Formik>
          </Box>
        </DialogContentText>
      </DialogContent>
    </>
  );
}
